import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (credentials) => {
    const response = await authAPI.login(credentials);
    const { token, user: userData } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);

    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const hasRole = useCallback((roles) => {
    if (!user) return false;
    if (typeof roles === 'string') {
      return user.role === roles;
    }
    return roles.includes(user.role);
  }, [user]);

  const isAdmin = useCallback(() => hasRole('admin'), [hasRole]);
  const isHRManager = useCallback(() => hasRole(['admin', 'hr_manager']), [hasRole]);
  const isLineManager = useCallback(() => hasRole(['admin', 'hr_manager', 'line_manager']), [hasRole]);

  // Memoise the context value so descendants don't re-render every time the
  // AuthProvider re-renders for unrelated reasons.
  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    hasRole,
    isAdmin,
    isHRManager,
    isLineManager,
  }), [user, loading, login, logout, hasRole, isAdmin, isHRManager, isLineManager]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
